import { useState } from 'react';

/**
 * Drag-to-reorder list, with arrows kept as a fallback.
 *
 * Drag is the primary gesture because moving an item several places is one
 * motion instead of one click per step. The arrows stay because native HTML5
 * drag is unreachable by keyboard and unreliable on touch — without them the
 * feature would be unusable for some people rather than merely slower.
 *
 * Uses the browser's own drag events rather than a library: the app has no
 * drag dependency installed, and a flat vertical list doesn't need one.
 *
 * @param {string[]} order  Item keys, in display order.
 * @param {(next: string[]) => void} onReorder  Called with the new order.
 * @param {(key, ctx) => ReactNode} renderItem  Row body; ctx has {index, isDragging}.
 */
export default function ReorderableList({
  order,
  onReorder,
  renderItem,
  disabled = false,
  gap = 6,
}) {
  const [draggingKey, setDraggingKey] = useState(null);
  const [overKey, setOverKey] = useState(null);

  const move = (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= order.length || to >= order.length) return;
    const next = [...order];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next);
  };

  const swap = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= order.length) return;
    const next = [...order];
    [next[index], next[target]] = [next[target], next[index]];
    onReorder(next);
  };

  const handleDrop = (targetKey) => {
    if (draggingKey == null) return;
    move(order.indexOf(draggingKey), order.indexOf(targetKey));
    setDraggingKey(null);
    setOverKey(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap }}>
      {order.map((key, index) => {
        const isDragging = draggingKey === key;
        const isOver = overKey === key && draggingKey !== key;
        return (
          <div
            key={key}
            draggable={!disabled}
            onDragStart={(e) => {
              if (disabled) return;
              setDraggingKey(key);
              e.dataTransfer.effectAllowed = 'move';
              // Firefox refuses to start a drag without payload.
              try {
                e.dataTransfer.setData('text/plain', key);
              } catch {
                /* ignore */
              }
            }}
            onDragOver={(e) => {
              if (disabled || draggingKey == null) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              setOverKey(key);
            }}
            onDragLeave={() => setOverKey((k) => (k === key ? null : k))}
            onDrop={(e) => {
              e.preventDefault();
              handleDrop(key);
            }}
            onDragEnd={() => {
              setDraggingKey(null);
              setOverKey(null);
            }}
            style={{
              opacity: isDragging ? 0.4 : 1,
              // A line on the edge the item would land against.
              boxShadow: isOver ? 'inset 0 2px 0 0 currentColor' : 'none',
              cursor: disabled ? 'default' : 'grab',
            }}
          >
            {renderItem(key, {
              index,
              isDragging,
              isFirst: index === 0,
              isLast: index === order.length - 1,
              moveUp: () => swap(index, -1),
              moveDown: () => swap(index, 1),
            })}
          </div>
        );
      })}
    </div>
  );
}
